import { useEffect, useMemo, useState, useRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, CreditCard, Calendar, Users, Check, Clock } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { roomService } from '../../services/roomService';
import { bookingService } from '../../services/bookingService';
import { holdService } from '../../services/holdService';
import { useViewport } from '../../hooks/useViewport';
import { useAuth } from '../../context/AuthContext';
import { UI, buttonStyle, fieldStyles, ui } from '../../utils/ui';
import { imgUrl } from '../../utils/imgUrl';

const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => {
  const hasValue = Boolean(value);
  return (
    <div
      ref={ref}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.(e);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 14px',
        borderRadius: 14,
        border: `1.5px solid ${UI.colors.border}`,
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        userSelect: 'none',
        width: '100%',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
    >
      <Calendar size={16} color={hasValue ? UI.colors.primary : '#9ca3af'} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 14, fontWeight: 600, color: hasValue ? UI.colors.text : '#9ca3af', flex: 1 }}>
        {value || placeholder}
      </span>
      <ChevronDown size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
    </div>
  );
});
DateInput.displayName = 'DateInput';

const StepDot = ({ step, current, label }) => {
  const done = current > step;
  const active = current === step;
  const bg = done || active ? UI.colors.primary : '#e5e7eb';
  const fg = done || active ? '#ffffff' : '#9ca3af';
  const ring = active ? '0 0 0 6px rgba(22,163,74,0.16)' : 'none';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 44, height: 44, borderRadius: 9999, backgroundColor: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, boxShadow: ring }}>
        {done ? <Check size={18} /> : step}
      </div>
      <span style={{ fontSize: 12, fontWeight: 900, color: current >= step ? UI.colors.primary : '#9ca3af', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
};

const CreateBooking = () => {
  const navigate = useNavigate();
  const { isDesktop } = useViewport();
  const { user } = useAuth();
  const commissionRate = Number(user?.commission_rate ?? 10);
  const [step, setStep] = useState(1);
  const [rooms, setRooms] = useState([]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const [formData, setFormData] = useState({
    customer_name: '',
    checkIn: today,
    checkOut: tomorrow,
    guests: 2,
    roomId: null,
    specialRequests: '',
  });
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Hold state
  const [holdId, setHoldId]               = useState(null);
  const [holdExpiresAt, setHoldExpiresAt] = useState(null);
  const [secondsLeft, setSecondsLeft]     = useState(null);
  const [creatingHold, setCreatingHold]   = useState(false);
  const [roomAvailability, setRoomAvailability] = useState({});
  const holdIdRef = useRef(null);

  useEffect(() => { holdIdRef.current = holdId; }, [holdId]);

  useEffect(() => {
    if (step === 2) {
      setLoadingRooms(true);
      roomService.getAvailableRooms()
        .then((res) => setRooms(res.data.rooms || []))
        .catch(() => toast.error('Failed to load rooms.'))
        .finally(() => setLoadingRooms(false));
    }
  }, [step]);

  // Check availability/hold status for all rooms on step 2
  useEffect(() => {
    if (step !== 2 || !rooms.length || !formData.checkIn || !formData.checkOut) return;
    const checkIn  = format(formData.checkIn,  'yyyy-MM-dd');
    const checkOut = format(formData.checkOut, 'yyyy-MM-dd');
    Promise.all(
      rooms.map(r =>
        roomService.checkAvailability(r.id, checkIn, checkOut)
          .then(res => [r.id, res.data])
          .catch(() => [r.id, { available: true, on_hold: false }])
      )
    ).then(results => setRoomAvailability(Object.fromEntries(results)));
  }, [step, rooms, formData.checkIn, formData.checkOut]);

  // Countdown timer on step 3
  useEffect(() => {
    if (step !== 3 || !holdExpiresAt) return;
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(holdExpiresAt) - Date.now()) / 1000));
      setSecondsLeft(secs);
      if (secs === 0) {
        toast.error('Your room hold has expired. Please select the room again.');
        setHoldId(null);
        setHoldExpiresAt(null);
        setSecondsLeft(null);
        setStep(2);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [step, holdExpiresAt]);

  // Release hold on unmount
  useEffect(() => {
    return () => {
      if (holdIdRef.current) {
        holdService.releaseHold(holdIdRef.current).catch(() => {});
      }
    };
  }, []);

  const nights = () => {
    if (!formData.checkIn || !formData.checkOut) return 0;
    const n = Math.ceil((formData.checkOut - formData.checkIn) / 86400000);
    return n > 0 ? n : 0;
  };

  const selectedRoom = useMemo(() => rooms.find((r) => r.id === formData.roomId), [rooms, formData.roomId]);
  const total = selectedRoom ? selectedRoom.price_per_night * nights() : 0;
  const fmtDate = (d) => (d ? format(d, 'dd MMM yyyy') : '—');

  const handleNext = async () => {
    setError('');
    if (step === 1) {
      if (!formData.customer_name.trim()) return setError('Please enter the customer name.');
      if (!formData.checkIn || !formData.checkOut) return setError('Please select check-in and check-out dates.');
    }
    if (step === 2) {
      if (!formData.roomId) return setError('Please select a room.');
      setCreatingHold(true);
      try {
        const res = await holdService.createHold({
          room_id:   formData.roomId,
          check_in:  format(formData.checkIn,  'yyyy-MM-dd'),
          check_out: format(formData.checkOut, 'yyyy-MM-dd'),
          guests:    Number(formData.guests),
        });
        setHoldId(res.data.hold.id);
        setHoldExpiresAt(res.data.hold.expires_at);
        setStep(3);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Room is currently unavailable. Please try another.');
      } finally {
        setCreatingHold(false);
      }
      return;
    }
    setStep((s) => s + 1);
  };

  const handleBackFromStep3 = async () => {
    if (holdId) {
      await holdService.releaseHold(holdId).catch(() => {});
      setHoldId(null);
      setHoldExpiresAt(null);
      setSecondsLeft(null);
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await bookingService.createBooking({
        customer_name: formData.customer_name,
        room_id: formData.roomId,
        check_in: format(formData.checkIn, 'yyyy-MM-dd'),
        check_out: format(formData.checkOut, 'yyyy-MM-dd'),
        guests: Number(formData.guests),
        special_requests: formData.specialRequests,
      });
      setHoldId(null);
      holdIdRef.current = null;
      toast.success('Booking created successfully!');
      navigate('/agent/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Booking failed. Please try again.');
      setSubmitting(false);
    }
  };

  const steps = [
    { n: 1, label: 'Customer & Dates' },
    { n: 2, label: 'Choose Room' },
    { n: 3, label: 'Confirm' },
  ];

  const wrapStyle = { display: 'flex', flexDirection: 'column', gap: 18 };
  const titleStyle = { fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text };
  const subtitleStyle = { margin: '8px 0 0', fontSize: 14, color: UI.colors.muted };

  const inputRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 14,
    border: `1.5px solid ${UI.colors.border}`,
    backgroundColor: '#ffffff',
  };

  const inputCoreStyle = {
    ...fieldStyles.input,
    border: 'none',
    padding: 0,
    borderRadius: 0,
    backgroundColor: 'transparent',
    flex: 1,
  };

  const spinner = (color = '#ffffff') => (
    <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: color, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
  );

  const fmtCountdown = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div style={wrapStyle}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {/* Header */}
      <div>
        <h1 style={titleStyle}>Create Booking</h1>
        <p style={subtitleStyle}>Book a room for a customer in 3 simple steps</p>
      </div>

      {error && (
        <div style={{ padding: 12, borderRadius: 14, border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#991b1b', fontSize: 13, fontWeight: 800 }}>
          {error}
        </div>
      )}

      {/* Steps */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
        {steps.map((st, i) => (
          <div key={st.n} style={{ display: 'flex', alignItems: 'center' }}>
            <StepDot step={st.n} current={step} label={st.label} />
            {i < steps.length - 1 && (
              <div
                style={{
                  width: isDesktop ? 120 : 56,
                  height: 2,
                  backgroundColor: step > st.n ? UI.colors.primary : UI.colors.border,
                  margin: '0 10px 18px',
                  borderRadius: 99,
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Content grid */}
      <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'minmax(0, 2fr) minmax(0, 1fr)' : '1fr', gap: 16, alignItems: 'start' }}>
        {/* Form Card */}
        <div style={ui.card}>
          <div style={{ padding: 22 }}>
            {step === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <h2 style={{ fontFamily: UI.font.heading, fontSize: 20, fontWeight: 900, margin: 0, color: UI.colors.text }}>Customer & Dates</h2>

                <div>
                  <label style={fieldStyles.label}>Customer Name</label>
                  <div className="input-row" style={{ ...inputRowStyle, padding: '11px 14px' }}>
                    <Users size={16} color={formData.customer_name ? UI.colors.primary : '#9ca3af'} style={{ flexShrink: 0 }} />
                    <input
                      type="text"
                      placeholder="Enter customer's full name"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      style={{ ...inputCoreStyle }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
                  <div>
                    <label style={fieldStyles.label}>Check-in Date</label>
                    <DatePicker
                      selected={formData.checkIn}
                      onChange={(date) => setFormData({ ...formData, checkIn: date, checkOut: formData.checkOut && date >= formData.checkOut ? null : formData.checkOut })}
                      minDate={new Date()}
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select check-in date"
                      customInput={<DateInput />}
                    />
                  </div>
                  <div>
                    <label style={fieldStyles.label}>Check-out Date</label>
                    <DatePicker
                      selected={formData.checkOut}
                      onChange={(date) => setFormData({ ...formData, checkOut: date })}
                      minDate={formData.checkIn ? new Date(formData.checkIn.getTime() + 86400000) : new Date()}
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select check-out date"
                      customInput={<DateInput />}
                    />
                  </div>
                </div>

                <div>
                  <label style={fieldStyles.label}>Number of Guests</label>
                  <div className="input-row" style={{ ...inputRowStyle, padding: '11px 14px' }}>
                    <Users size={16} color="#9ca3af" style={{ flexShrink: 0 }} />
                    <select
                      value={formData.guests}
                      onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                      style={{ ...inputCoreStyle, appearance: 'none', cursor: 'pointer', fontWeight: 600 }}
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} color="#9ca3af" style={{ flexShrink: 0, pointerEvents: 'none' }} />
                  </div>
                </div>

                <button type="button" onClick={handleNext} style={{ ...buttonStyle('primary'), width: '100%', padding: '12px 16px', borderRadius: 14 }}>
                  Continue to Room Selection <ChevronRight size={18} />
                </button>
              </div>
            )}

            {step === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <h2 style={{ fontFamily: UI.font.heading, fontSize: 20, fontWeight: 900, margin: 0, color: UI.colors.text }}>Choose Room</h2>
                {loadingRooms ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '22px 0' }}>
                    <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                    {rooms.map((room) => {
                      const avail   = roomAvailability[room.id];
                      const onHold  = avail?.on_hold === true;
                      const unavail = avail !== undefined && !avail.available && !onHold;
                      const blocked = onHold || unavail;
                      const selected = !blocked && formData.roomId === room.id;
                      return (
                        <div
                          key={room.id}
                          onClick={() => !blocked && setFormData({ ...formData, roomId: room.id })}
                          style={{
                            borderRadius: 18,
                            overflow: 'hidden',
                            cursor: blocked ? 'not-allowed' : 'pointer',
                            border: `2px solid ${selected ? UI.colors.primary : UI.colors.border}`,
                            boxShadow: selected ? '0 10px 26px rgba(22,163,74,0.18)' : 'none',
                            backgroundColor: '#ffffff',
                            position: 'relative',
                            opacity: blocked ? 0.65 : 1,
                          }}
                        >
                          <img
                            src={imgUrl(room.images?.[0]) || 'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=400&h=300&fit=crop'}
                            alt={room.name}
                            style={{ width: '100%', height: 120, objectFit: 'cover', backgroundColor: '#f3f4f6' }}
                          />
                          <div style={{ padding: 12 }}>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: UI.colors.text }}>{room.name}</p>
                            <p style={{ margin: '6px 0 0', fontSize: 12, color: UI.colors.muted, fontWeight: 700 }}>Up to {room.capacity} guests</p>
                            <p style={{ margin: '8px 0 0', fontWeight: 900, color: UI.colors.primary }}>
                              ₹{Number(room.price_per_night).toLocaleString()}
                              <span style={{ fontSize: 12, color: UI.colors.muted, fontWeight: 700 }}> /night</span>
                            </p>
                          </div>
                          {selected && (
                            <div style={{ position: 'absolute', top: 10, right: 10, padding: '4px 10px', borderRadius: 9999, backgroundColor: UI.colors.primary, color: '#ffffff', fontSize: 11, fontWeight: 900, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Check size={12} /> Selected
                            </div>
                          )}
                          {onHold && (
                            <div style={{ position: 'absolute', top: 10, right: 10, padding: '4px 10px', borderRadius: 9999, backgroundColor: '#d97706', color: '#ffffff', fontSize: 11, fontWeight: 900, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Clock size={12} /> On Hold
                            </div>
                          )}
                          {unavail && (
                            <div style={{ position: 'absolute', top: 10, right: 10, padding: '4px 10px', borderRadius: 9999, backgroundColor: '#dc2626', color: '#ffffff', fontSize: 11, fontWeight: 900 }}>
                              Unavailable
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
                  <button type="button" onClick={() => setStep(1)} style={{ ...buttonStyle('outline'), padding: '12px 16px', borderRadius: 14, flex: 1 }}>
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={creatingHold}
                    style={{ ...buttonStyle('primary'), padding: '12px 16px', borderRadius: 14, flex: 1, opacity: creatingHold ? 0.75 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  >
                    {creatingHold ? spinner() : <ChevronRight size={18} />}
                    {creatingHold ? 'Holding Room…' : 'Continue'}
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <h2 style={{ fontFamily: UI.font.heading, fontSize: 20, fontWeight: 900, margin: 0, color: UI.colors.text }}>Confirm Booking</h2>

                {/* Countdown banner */}
                {holdExpiresAt && secondsLeft !== null && (
                  <div style={{
                    backgroundColor: secondsLeft < 60 ? '#fee2e2' : '#dcfce7',
                    border: `1px solid ${secondsLeft < 60 ? '#fca5a5' : '#86efac'}`,
                    borderRadius: 12, padding: '12px 18px',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <Clock size={18} color={secondsLeft < 60 ? '#dc2626' : '#16a34a'} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: secondsLeft < 60 ? '#dc2626' : '#16a34a' }}>
                      Room held — complete booking within {fmtCountdown(secondsLeft)}
                    </span>
                  </div>
                )}

                <div style={{ padding: 16, borderRadius: 18, backgroundColor: '#f3f4f6', border: `1px solid ${UI.colors.border}` }}>
                  {[
                    ['Customer', formData.customer_name || '—'],
                    ['Room', selectedRoom?.name],
                    ['Check-in', fmtDate(formData.checkIn)],
                    ['Check-out', fmtDate(formData.checkOut)],
                    ['Guests', formData.guests],
                    ['Nights', nights()],
                    ['Rate', `₹${Number(selectedRoom?.price_per_night || 0).toLocaleString()}/night`],
                    ['Total', `₹${total.toLocaleString()}`],
                    [`Your Commission (${commissionRate}%)`, `₹${(total * commissionRate / 100).toLocaleString()}`],
                  ].map(([label, val], idx, arr) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: idx < arr.length - 1 ? `1px solid ${UI.colors.border}` : 'none', fontSize: 13 }}>
                      <span style={{ color: UI.colors.muted, fontWeight: 800 }}>{label}</span>
                      <span style={{ color: label.includes('Commission') ? '#16a34a' : UI.colors.text, fontWeight: 900 }}>{val ?? '—'}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <label style={fieldStyles.label}>Special Requests (Optional)</label>
                  <textarea
                    rows={3}
                    value={formData.specialRequests}
                    onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                    placeholder="Any special requests..."
                    style={{ ...fieldStyles.input, minHeight: 90, resize: 'vertical', padding: '12px 12px', borderRadius: 14 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" onClick={handleBackFromStep3} style={{ ...buttonStyle('outline'), padding: '12px 16px', borderRadius: 14, flex: 1 }}>
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={{ ...buttonStyle('primary'), padding: '12px 16px', borderRadius: 14, flex: 1, opacity: submitting ? 0.75 : 1 }}
                  >
                    {submitting ? spinner() : <CreditCard size={18} />}
                    {submitting ? 'Creating...' : 'Create Booking'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ ...ui.card, position: isDesktop ? 'sticky' : 'static', top: 24 }}>
          <div style={{ padding: 18 }}>
            <h3 style={{ fontFamily: UI.font.heading, fontSize: 18, fontWeight: 900, color: UI.colors.text, margin: '0 0 12px' }}>Summary</h3>
            {selectedRoom && (
              <div style={{ marginBottom: 14 }}>
                <img
                  src={imgUrl(selectedRoom.images?.[0]) || 'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=400&h=300&fit=crop'}
                  alt={selectedRoom.name}
                  style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 16, marginBottom: 10, backgroundColor: '#f3f4f6' }}
                />
                <p style={{ margin: 0, fontWeight: 900, color: UI.colors.text, fontSize: 13 }}>{selectedRoom.name}</p>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
              {formData.checkIn && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ color: UI.colors.muted, fontWeight: 800 }}>Check-in</span>
                  <span style={{ color: UI.colors.text, fontWeight: 900 }}>{fmtDate(formData.checkIn)}</span>
                </div>
              )}
              {formData.checkOut && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ color: UI.colors.muted, fontWeight: 800 }}>Check-out</span>
                  <span style={{ color: UI.colors.text, fontWeight: 900 }}>{fmtDate(formData.checkOut)}</span>
                </div>
              )}
              {nights() > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ color: UI.colors.muted, fontWeight: 800 }}>Nights</span>
                  <span style={{ color: UI.colors.text, fontWeight: 900 }}>{nights()}</span>
                </div>
              )}
              {selectedRoom && nights() > 0 && (
                <>
                  <div style={{ height: 1, backgroundColor: UI.colors.border, margin: '4px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ fontWeight: 900, color: UI.colors.text }}>Total</span>
                    <span style={{ fontWeight: 900, color: UI.colors.text }}>₹{total.toLocaleString()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ fontWeight: 900, color: '#16a34a' }}>Your Commission ({commissionRate}%)</span>
                    <span style={{ fontWeight: 900, color: '#16a34a' }}>₹{(total * commissionRate / 100).toLocaleString()}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBooking;
